/**
 * Auto hit-test probe for Capacitor iOS (Xcode / Safari console).
 * Logs document.elementFromPoint() at the center of known tap targets on /app-beta load.
 */

import { isCapacitorIosApp } from '@/lib/auth/capacitorSignIn'

export type TapProbeTargetId =
  | 'profile-tab'
  | 'travel-tab'
  | 'home-dog-avatar'
  | 'guest-apple-cta'

export type TapProbeElementInfo = {
  tagName: string
  id: string | null
  className: string | null
  zIndex: string
  pointerEvents: string
  position: string
  opacity: string
  rect: {
    x: number
    y: number
    width: number
    height: number
  }
  dataTapProbe: string | null
  text: string | null
}

const PROBE_TARGETS: TapProbeTargetId[] = [
  'profile-tab',
  'travel-tab',
  'home-dog-avatar',
  'guest-apple-cta',
]

/** True when Capacitor iOS should auto-log tap interceptors on /app-beta load (Xcode console). */
export function shouldAutoCapacitorTapProbe(): boolean {
  if (typeof window === 'undefined') return false
  if (!isCapacitorIosApp()) return false
  return window.location.pathname.startsWith('/app-beta')
}

export function describeTapProbeElement(el: Element | null): TapProbeElementInfo | null {
  if (!el || !(el instanceof HTMLElement)) return null
  const rect = el.getBoundingClientRect()
  const style = window.getComputedStyle(el)
  const text = (el.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 80)
  return {
    tagName: el.tagName.toLowerCase(),
    id: el.id || null,
    className: typeof el.className === 'string' ? el.className.slice(0, 160) : null,
    zIndex: style.zIndex,
    pointerEvents: style.pointerEvents,
    position: style.position,
    opacity: style.opacity,
    rect: {
      x: Math.round(rect.x),
      y: Math.round(rect.y),
      width: Math.round(rect.width),
      height: Math.round(rect.height),
    },
    dataTapProbe: el.getAttribute('data-tap-probe'),
    text: text || null,
  }
}

function findProbeTarget(targetId: TapProbeTargetId): HTMLElement | null {
  const byData = document.querySelector<HTMLElement>(`[data-tap-probe="${targetId}"]`)
  if (byData) return byData

  if (targetId === 'profile-tab' || targetId === 'travel-tab') {
    const nav = document.querySelector('#bottom-navigation nav[aria-label="Main tabs"]')
    const buttons = nav?.querySelectorAll('button')
    if (buttons && buttons.length > 0) {
      return buttons[buttons.length - 1] as HTMLElement
    }
  }

  if (targetId === 'home-dog-avatar') {
    const avatar = document.querySelector<HTMLElement>(
      '[data-tap-probe="home-dog-avatar"], [data-home-dog-avatar="true"]'
    )
    if (avatar) return avatar
  }

  if (targetId === 'guest-apple-cta') {
    const banner = document.querySelector('.bg-amber-50.border-b.border-amber-200 button')
    if (banner instanceof HTMLElement) return banner
  }

  return null
}

function hitTestAtCenter(el: HTMLElement): {
  x: number
  y: number
  hit: TapProbeElementInfo | null
  intercepts: boolean
} {
  const rect = el.getBoundingClientRect()
  const x = Math.round(rect.left + rect.width / 2)
  const y = Math.round(rect.top + rect.height / 2)
  const hitEl = document.elementFromPoint(x, y)
  const intercepts = !(hitEl === el || (hitEl != null && el.contains(hitEl)))
  return { x, y, hit: describeTapProbeElement(hitEl), intercepts }
}

export function logCapacitorTapProbeTarget(targetId: TapProbeTargetId): boolean {
  const expected = findProbeTarget(targetId)
  if (!expected) {
    console.warn('[CapacitorTapProbe]', targetId, 'expected element not found in DOM')
    return false
  }

  const expectedInfo = describeTapProbeElement(expected)
  const { x, y, hit, intercepts } = hitTestAtCenter(expected)

  console.log('[CapacitorTapProbe]', {
    targetId,
    probePoint: { x, y },
    expected: expectedInfo,
    elementFromPoint: hit,
    interceptsTap: intercepts,
    href: window.location.href,
    ts: new Date().toISOString(),
  })

  if (intercepts && hit) {
    console.warn('[CapacitorTapProbe] TAP BLOCKED', targetId, 'hit element is not the expected control', hit)
  }

  return true
}

export function runCapacitorTapProbeOnLoad(): () => void {
  if (!shouldAutoCapacitorTapProbe()) return () => {}
  if (!window.location.pathname.startsWith('/app-beta')) return () => {}

  let cancelled = false
  const delays = [1500, 2500, 5000]
  const timers: ReturnType<typeof setTimeout>[] = []

  const run = (pass: number) => {
    if (cancelled) return
    console.log('[CapacitorTapProbe] pass', pass, PROBE_TARGETS.join(', '))
    for (const targetId of PROBE_TARGETS) {
      logCapacitorTapProbeTarget(targetId)
    }
  }

  delays.forEach((delay, index) => {
    timers.push(
      window.setTimeout(() => {
        run(index + 1)
      }, delay)
    )
  })

  console.log('[CapacitorTapProbe] scheduled on load (Capacitor iOS dev)', {
    pathname: window.location.pathname,
    nodeEnv: process.env.NODE_ENV,
    hostname: window.location.hostname,
  })

  return () => {
    cancelled = true
    timers.forEach((t) => window.clearTimeout(t))
  }
}
